import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { WhatsAppStatusIndicator } from './WhatsAppStatusIndicator';

export function Header() {
  const navigate = useNavigate();
  const { user, activeSite, changeSite } = useAuth();
  const { requestNavigation } = useUnsavedChanges();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isChangingSite, setIsChangingSite] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleSiteChange = async (siteId: string) => {
    if (siteId === activeSite?.id) {
      setIsDropdownOpen(false);
      return;
    }

    // Confirmar si hay cambios sin guardar (ahora usa modal)
    requestNavigation(async () => {
      setIsChangingSite(true);
      setIsDropdownOpen(false);
      try {
        await changeSite(siteId);
        navigate('/dashboard', { replace: true });
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } catch (error) {
        console.error('Error changing site:', error);
      } finally {
        setIsChangingSite(false);
      }
    });
  };
  
  const hasMultipleSites = (user?.sites?.length || 0) > 1;
  
  return (
    <header className="h-16 flex-shrink-0 bg-white border-b border-slate-200 px-6 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <h2 className="text-lg font-semibold text-slate-900">
        </h2>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => hasMultipleSites && setIsDropdownOpen(!isDropdownOpen)}
            disabled={isChangingSite || !hasMultipleSites}
            className={`
              flex items-center gap-3 px-4 py-2 rounded-xl border
              transition-all duration-200
              ${hasMultipleSites 
                ? 'border-slate-200 hover:border-primary hover:bg-primary/5 cursor-pointer' 
                : 'border-transparent cursor-default'
              }
              ${isDropdownOpen ? 'border-primary bg-primary/5' : ''}
              ${isChangingSite ? 'opacity-50' : ''}
            `}
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-primary text-lg">
                location_on
              </span>
            </div>
            
            <div className="text-left">
              <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wide">
                Sede activa
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {isChangingSite ? 'Cambiando...' : activeSite?.name || 'Sin sede'}
              </p>
            </div>
            
            {hasMultipleSites && (
              <span className={`
                material-symbols-outlined text-slate-400 text-lg ml-1
                transition-transform duration-200
                ${isDropdownOpen ? 'rotate-180' : ''}
              `}>
                expand_more
              </span>
            )}
          </button>
          
          {isDropdownOpen && hasMultipleSites && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden animate-fade-in">
              <div className="p-2">
                <p className="px-3 py-2 text-[10px] text-slate-400 uppercase font-bold">
                  Seleccionar sede
                </p>
                {user?.sites.map(site => (
                  <button
                    key={site.id}
                    onClick={() => handleSiteChange(site.id)}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                      transition-all duration-150
                      ${site.id === activeSite?.id 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-slate-700 hover:bg-slate-50'
                      }
                    `}
                  >
                    <div className={`
                      w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold
                      ${site.id === activeSite?.id 
                        ? 'bg-primary text-white' 
                        : 'bg-slate-100 text-slate-600'
                      }
                    `}>
                      {site.code}
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-medium">{site.name}</p>
                      {site.address && (
                        <p className="text-[10px] text-slate-400 truncate">{site.address}</p>
                      )}
                    </div>
                    {site.id === activeSite?.id && (
                      <span className="material-symbols-outlined text-primary text-lg">
                        check_circle
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <WhatsAppStatusIndicator />
        <div className="h-8 w-px bg-slate-200" />
        
        <button className="relative p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
          <span className="material-symbols-outlined text-xl">notifications</span>
        </button>
        
        <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors">
          <span className="material-symbols-outlined text-xl">help_outline</span>
        </button>
      </div>
    </header>
  );
}

export default Header;
